import { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertCircle,
  DatabaseZap,
  Edit,
  Eye,
  GitBranch,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertDescription,
  DataTable,
  Pagination,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  SearchInput,
  useAuth,
  useLocale,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useUrlPagination } from '../../hooks/useUrlPagination';
import { getDefaultPipelines } from '../../constants/referenceDefaults';
import { usePipelineListQuery } from '../../hooks/usePipelineQueries';
import pipelineService from '../../services/pipeline.service';
import { PipelineFormDialog } from '../../components/PipelineFormDialog';
import { useATSRole } from '../../hooks/useATSRole';
import type { Pipeline, PipelineRound } from '../../types';

export function PipelineListPage() {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();
  const { t } = useTranslation('ats');
  const { locale } = useLocale();
  const defaultPipelines = useMemo(() => getDefaultPipelines(locale), [locale]);
  const { page, size, sortBy, order, filters, setPage, setSize, setSort, setFilters } =
    useUrlPagination({ filterKeys: ['search'] });
  const searchValue = filters.search as string | undefined;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingPipeline, setEditingPipeline] = useState<Pipeline | null>(null);
  const [showDefaultDialog, setShowDefaultDialog] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);

  // React Query for Pipeline list
  const {
    data: pipelineResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = usePipelineListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page,
    size,
    sortBy,
    order,
    search: searchValue,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 5000,
  });

  // Extract data from response
  const pipelines = pipelineResponse?.data?.items || [];
  const totalItems = pipelineResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / size);

  const handleCreateClick = useCallback(() => {
    setFormMode('create');
    setEditingPipeline(null);
    setShowFormDialog(true);
  }, []);

  const handleEditClick = useCallback((record: Pipeline) => {
    setFormMode('edit');
    setEditingPipeline(record);
    setShowFormDialog(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowFormDialog(false);
    setEditingPipeline(null);
  }, []);

  const handleFormSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDeleteClick = useCallback((record: Pipeline) => {
    setSelectedPipeline(record);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedPipeline || !user || !currentWorkspace) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(selectedPipeline.id));

    try {
      const token = await user.getIdToken();
      await pipelineService.deletePipeline(selectedPipeline.id, currentWorkspace.id, token);
      await refetch();
      setShowDeleteDialog(false);
      setSelectedPipeline(null);
    } catch (err: unknown) {
      let errorMessage = t('config.unknownError');
      if (axios.isAxiosError(err)) {
        errorMessage =
          err.response?.data?.message || err.response?.data?.detail || err.message || errorMessage;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        if (selectedPipeline) {
          newSet.delete(selectedPipeline.id);
        }
        return newSet;
      });
    }
  };

  const handleDialogClose = () => {
    if (!isDeleting) {
      setShowDeleteDialog(false);
      setSelectedPipeline(null);
      setDeleteError(null);
    }
  };

  const handleViewClick = useCallback(
    (record: Pipeline) => {
      navigate(`/ats/pipelines/${record.id}`);
    },
    [navigate]
  );

  const handleCreateDefaults = async () => {
    if (!user || !currentWorkspace) return;
    setIsCreatingDefaults(true);
    try {
      const token = await user.getIdToken();
      await pipelineService.bulkCreatePipelines(currentWorkspace.id, token, defaultPipelines);
      await refetch();
      setShowDefaultDialog(false);
    } catch {
      // surfaced via refetch
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  // Define table columns
  const columns: DataTableColumn<Pipeline>[] = [
    {
      key: 'name',
      title: t('pipelineConfig.columnName'),
      sortable: true,
      render: (value: string, record: Pipeline) => (
        <button
          onClick={() => handleViewClick(record)}
          className="font-medium text-teal-600 hover:text-teal-700 hover:underline text-left"
        >
          {value}
        </button>
      ),
    },
    {
      key: 'rounds',
      title: t('pipelineConfig.columnRounds'),
      render: (rounds: PipelineRound[]) => {
        if (!rounds || rounds.length === 0) {
          return <span className="text-gray-400">{t('pipelineConfig.emptyRounds')}</span>;
        }
        const roundNames = rounds.map((r) => r.name).join(' → ');
        return (
          <span className="text-gray-600 truncate max-w-md block" title={roundNames}>
            {roundNames}
          </span>
        );
      },
    },
    {
      key: 'created_at',
      title: t('config.columnCreated'),
      width: 120,
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString(),
    },
  ];

  // Define table actions (edit/delete only for leader+)
  const actions: DataTableAction<Pipeline>[] = [
    {
      key: 'view',
      label: t('pipelineConfig.view'),
      icon: <Eye className="h-4 w-4" />,
      onClick: handleViewClick,
      inline: true,
    },
    ...(isLeaderOrAbove
      ? [
          {
            key: 'edit',
            label: t('config.edit'),
            icon: <Edit className="h-4 w-4" />,
            onClick: handleEditClick,
            inline: true,
          },
          {
            key: 'delete',
            label: t('config.delete'),
            icon: <Trash2 className="h-4 w-4" />,
            loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
            onClick: handleDeleteClick,
            loading: (record: Pipeline) => deletingIds.has(record.id),
            destructive: true,
            inline: true,
          },
        ]
      : []),
  ];

  if (isLoading && !pipelineResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GitBranch className="h-8 w-8 text-teal-600" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">{t('pipelineConfig.pageTitle')}</h1>
              {isFetching && !isLoading && (
                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-500">{t('pipelineConfig.pageDescription')}</p>
          </div>
        </div>
        {isLeaderOrAbove && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDefaultDialog(true)}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <DatabaseZap className="h-4 w-4" />
              {t('config.createDefaults')}
            </button>
            <button
              onClick={handleCreateClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('pipelineConfig.newOne')}
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <SearchInput
        placeholder={t('pipelineConfig.searchPlaceholder')}
        onSearch={(value) => setFilters({ ...filters, search: value || undefined })}
        initialValue={searchValue || ''}
      />

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('pipelineConfig.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <div className="space-y-4">
        <DataTable
          columns={columns}
          data={pipelines}
          loading={isLoading && !pipelineResponse}
          actions={actions}
          rowKey={(record) => record.id}
          emptyMessage={t('pipelineConfig.empty')}
          onSort={setSort}
          sortKey={sortBy}
          sortDirection={order}
        />

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          pageSize={size}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          showTotal={true}
          showPageSizeSelector={true}
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={handleDialogClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipelineConfig.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('pipelineConfig.deleteBody')}</AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.deleting')}
                </>
              ) : (
                t('config.delete')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Pipeline Dialog */}
      <PipelineFormDialog
        open={showFormDialog}
        onClose={handleFormClose}
        mode={formMode}
        pipeline={editingPipeline}
        onSuccess={handleFormSuccess}
      />

      {/* Create Defaults Dialog */}
      <AlertDialog
        open={showDefaultDialog}
        onOpenChange={(open) => !isCreatingDefaults && setShowDefaultDialog(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipelineConfig.defaultsTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>{t('pipelineConfig.defaultsDescription')}</p>
                <div className="mt-3 space-y-3 max-h-60 overflow-y-auto">
                  {defaultPipelines.map((p) => (
                    <div
                      key={p.name}
                      className="rounded border border-gray-200 bg-gray-50 p-3"
                    >
                      <div className="text-sm font-medium text-gray-900">{p.name}</div>
                      <div className="mt-1 text-xs text-gray-600">
                        {t('pipelineConfig.defaultsRoundsLabel')}{' '}
                        {p.rounds.map((r) => r.name).join(' → ')}
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-sm">{t('config.createDefaultsSkipNote')}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingDefaults}>{t('config.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => {
                e.preventDefault();
                handleCreateDefaults();
              }}
              disabled={isCreatingDefaults || defaultPipelines.length === 0}
            >
              {isCreatingDefaults ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : (
                t('config.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
